import { useState, useRef, useEffect, useCallback } from "react";

export interface UseStickyPopoverOptions {
  onClose?: () => void;
}

export function useStickyPopover<T extends HTMLElement = HTMLDivElement>(
  options?: UseStickyPopoverOptions
) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const containerRef = useRef<T>(null);
  const onCloseRef = useRef(options?.onClose);
  onCloseRef.current = options?.onClose;

  const close = useCallback(() => {
    setIsPinned(false);
    setIsOpen(false);
    onCloseRef.current?.();
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isPinned) {
      setIsOpen(true);
    }
  }, [isPinned]);

  const handleMouseLeave = useCallback(() => {
    if (!isPinned) {
      setIsOpen(false);
    }
  }, [isPinned]);

  const handleToggleClick = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setIsPinned((prevPinned) => {
      const nextPinned = !prevPinned;
      setIsOpen(nextPinned);
      return nextPinned;
    });
  }, []);

  useEffect(() => {
    if (!isOpen && !isPinned) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isPinned, close]);

  return {
    isOpen,
    isPinned,
    containerRef,
    handleMouseEnter,
    handleMouseLeave,
    handleToggleClick,
    close,
  };
}
